import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import {
  LayoutDashboard, Users, BookOpen, UserCog, ShieldCheck,
  CreditCard, Pause, Play, XCircle, ArrowUpDown,
  Search, Loader2, PoundSterling, ClipboardList, BarChart3,
  FileText, Calendar, Settings, Video, BookMarked, Gamepad2,
  PenTool, Languages, Eye, Sparkles, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: "standard_1", name: "Standard 1 Subject", price: 89, cat: "Standard" },
  { id: "standard_2", name: "Standard 2 Subjects", price: 159, cat: "Standard" },
  { id: "standard_3", name: "Standard 3 Subjects", price: 179, cat: "Standard" },
  { id: "gcse_1", name: "GCSE 1 Subject", price: 99, cat: "GCSE" },
  { id: "gcse_2", name: "GCSE 2 Subjects", price: 179, cat: "GCSE" },
  { id: "gcse_3", name: "GCSE 3 Subjects", price: 199, cat: "GCSE" },
  { id: "eleven_plus", name: "11+ Preparation", price: 119, cat: "Special" },
  { id: "independent", name: "Independent Study", price: 49, cat: "Special" },
  { id: "esol", name: "ESOL", price: 69, cat: "Special" },
];

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  cancelling: "bg-red-100 text-red-700",
  cancelled: "bg-slate-200 text-slate-600",
  expired: "bg-slate-200 text-slate-600",
};

const sidebarItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/quality', label: 'Quality Assurance', icon: ShieldCheck },
  { path: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList },
  { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
  { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
];

const SubscriptionManagement = () => {
  const { api } = useAuth();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, sub: null });
  const [actionReason, setActionReason] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [acting, setActing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        api.get(`/subscriptions/list?status=${filter}`),
        api.get('/subscriptions/stats/summary'),
      ]);
      setSubs(subsRes.data.subscriptions);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = subs.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.child_name?.toLowerCase().includes(q) ||
      s.student_email?.toLowerCase().includes(q) ||
      s.plan_name?.toLowerCase().includes(q)
    );
  });

  const openAction = (type, sub) => {
    setActionDialog({ open: true, type, sub });
    setActionReason('');
    setActionPlan(sub.plan_id || '');
  };

  const closeAction = () => {
    setActionDialog({ open: false, type: null, sub: null });
    setActionReason('');
  };

  const performAction = async () => {
    const { type, sub } = actionDialog;
    setActing(true);
    try {
      if (type === 'pause') {
        await api.post(`/subscriptions/${sub.id}/pause`, { reason: actionReason });
        toast.success(`${sub.child_name}'s subscription paused`);
      } else if (type === 'resume') {
        await api.post(`/subscriptions/${sub.id}/resume`);
        toast.success(`${sub.child_name}'s subscription resumed`);
      } else if (type === 'cancel') {
        await api.post(`/subscriptions/${sub.id}/cancel`, { reason: actionReason });
        toast.success(`${sub.child_name}'s subscription will cancel at end of period`);
      } else if (type === 'change') {
        await api.post(`/subscriptions/${sub.id}/change-plan`, { new_plan_id: actionPlan });
        const plan = PLANS.find(p => p.id === actionPlan);
        toast.success(`Changed to ${plan?.name || actionPlan}`);
      }
      closeAction();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  return (
    <ProLayout title="Subscription Management" sidebarItems={sidebarItems}>
      <div data-testid="subscription-management">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="Active" value={stats.active} color="text-emerald-700" />
            <StatCard label="Paused" value={stats.paused} color="text-amber-700" />
            <StatCard label="Cancelling" value={stats.cancelling} color="text-red-700" />
            <StatCard label="Cancelled" value={stats.cancelled} color="text-slate-500" />
            <StatCard label="MRR" value={`£${stats.mrr}`} color="text-purple-700" icon={PoundSterling} />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="search-subscriptions"
              placeholder="Search by name, email, or plan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="cancelling">Cancelling</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-emerald-600 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-500">No subscriptions found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Student</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Plan</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Period End</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(sub => (
                    <tr key={sub.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sub-row-${sub.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{sub.child_name || sub.student_name}</p>
                        <p className="text-xs text-slate-400">{sub.student_email || sub.parent_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-slate-700">{sub.plan_name}</p>
                        {sub.subjects?.length > 0 && (
                          <p className="text-xs text-slate-400">{sub.subjects.join(', ')}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-800">
                        £{sub.amount}<span className="text-xs font-normal text-slate-400">/mo</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[sub.status] || STATUS_STYLES.active}`}>
                          {sub.status}
                        </span>
                        {sub.cancel_at_period_end && (
                          <p className="text-xs text-red-500 mt-1">Ends: {new Date(sub.effective_cancel_date).toLocaleDateString()}</p>
                        )}
                        {sub.paused_at && (
                          <p className="text-xs text-amber-500 mt-1">Since: {new Date(sub.paused_at).toLocaleDateString()}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          {sub.status === 'active' && (
                            <>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                                data-testid={`pause-btn-${sub.id}`} onClick={() => openAction('pause', sub)}>
                                <Pause className="w-3 h-3" /> Pause
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`cancel-btn-${sub.id}`} onClick={() => openAction('cancel', sub)}>
                                <XCircle className="w-3 h-3" /> Cancel
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                data-testid={`change-btn-${sub.id}`} onClick={() => openAction('change', sub)}>
                                <ArrowUpDown className="w-3 h-3" /> Change
                              </Button>
                            </>
                          )}
                          {sub.status === 'paused' && (
                            <>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                data-testid={`resume-btn-${sub.id}`} onClick={() => openAction('resume', sub)}>
                                <Play className="w-3 h-3" /> Resume
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openAction('cancel', sub)}>
                                <XCircle className="w-3 h-3" /> Cancel
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => openAction('change', sub)}>
                                <ArrowUpDown className="w-3 h-3" /> Change
                              </Button>
                            </>
                          )}
                          {sub.status === 'cancelling' && (
                            <span className="text-xs text-red-500 italic">Cancelling at period end</span>
                          )}
                          {(sub.status === 'cancelled' || sub.status === 'expired') && (
                            <span className="text-xs text-slate-400 italic">Inactive</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Dialog */}
        <Dialog open={actionDialog.open} onOpenChange={(open) => !open && closeAction()}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="capitalize">
                {actionDialog.type === 'pause' && 'Pause Subscription'}
                {actionDialog.type === 'resume' && 'Resume Subscription'}
                {actionDialog.type === 'cancel' && 'Cancel Subscription'}
                {actionDialog.type === 'change' && 'Change Plan'}
              </DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-4">
              <p className="text-sm text-slate-600">
                Student: <strong>{actionDialog.sub?.child_name}</strong>
                <br />Current plan: <strong>{actionDialog.sub?.plan_name}</strong> (£{actionDialog.sub?.amount}/mo)
              </p>

              {actionDialog.type === 'cancel' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  Subscription will cancel at the end of the current billing period. The student keeps access until then.
                </div>
              )}

              {(actionDialog.type === 'pause' || actionDialog.type === 'cancel') && (
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Reason (optional)</label>
                  <Input
                    data-testid="action-reason"
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder={actionDialog.type === 'pause' ? 'e.g. Holiday break' : 'e.g. Moving schools'}
                  />
                </div>
              )}

              {actionDialog.type === 'resume' && (
                <p className="text-emerald-700 text-sm bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                  This will reactivate the subscription immediately.
                </p>
              )}

              {actionDialog.type === 'change' && (
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-2">New Plan</label>
                  <Select value={actionPlan} onValueChange={setActionPlan}>
                    <SelectTrigger data-testid="change-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLANS.map(p => (
                        <SelectItem key={p.id} value={p.id} disabled={p.id === actionDialog.sub?.plan_id}>
                          {p.name} — £{p.price}/mo {p.id === actionDialog.sub?.plan_id ? '(current)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {actionPlan && actionPlan !== actionDialog.sub?.plan_id && (
                    <p className="text-xs mt-2 text-blue-600">
                      {(() => {
                        const newPlan = PLANS.find(p => p.id === actionPlan);
                        const diff = (newPlan?.price || 0) - (actionDialog.sub?.amount || 0);
                        if (diff > 0) return `Upgrade: +£${diff}/mo`;
                        if (diff < 0) return `Downgrade: -£${Math.abs(diff)}/mo`;
                        return 'Same price';
                      })()}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={closeAction} data-testid="action-cancel-btn">Cancel</Button>
              <Button
                data-testid="action-confirm-btn"
                disabled={acting || (actionDialog.type === 'change' && actionPlan === actionDialog.sub?.plan_id)}
                onClick={performAction}
                className={
                  actionDialog.type === 'cancel' ? 'bg-red-600 hover:bg-red-700' :
                  actionDialog.type === 'pause' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-emerald-600 hover:bg-emerald-700'
                }
              >
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  actionDialog.type === 'pause' ? 'Pause Subscription' :
                  actionDialog.type === 'resume' ? 'Resume Now' :
                  actionDialog.type === 'cancel' ? 'Cancel at Period End' :
                  'Change Plan'
                }
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ProLayout>
  );
};

const StatCard = ({ label, value, color = "text-slate-800", icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
    </div>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);

export default SubscriptionManagement;
